/* eslint-disable no-console */
import { Capacitor } from '@capacitor/core';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { NBXStorage } from '@nbx/frontend-helpers/storage';
import { authTypes } from '@nbx/frontend-helpers/subscribe';

// NODE_ENV is `production` in every deployed environment, so it cannot tell dev from prod.
const resolveEnv = () => {
  const { hostname } = window.location;
  if (/(^|\.)dev\.nbx\.com$/.test(hostname)) return 'dev';
  if (/(^|\.)nbx\.com$/.test(hostname)) return 'prod';
  return 'local';
};

// Shared by RUM and Logs — both event shapes expose view.url.
const redactSensitiveUrls = event => {
  if (event.view?.url) {
    event.view.url = event.view.url
      .replace(/verifyLoginEmail=[^&]*/, 'verifyLoginEmail=REDACTED')
      .replace(/verifyLoginIdToken=[^&]*/, 'verifyLoginIdToken=REDACTED');
  }
  return event;
};

const RUM_CONFIG = {
  base: {
    env: resolveEnv(),
    site: 'datadoghq.eu',
    sessionSampleRate: 0,
    sessionReplaySampleRate: 0,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingUrls: [],
    beforeSend: redactSensitiveUrls
  },
  mobile: {
    applicationId: 'fefc0b9c-167c-47e2-ac94-38d49a5dfa32',
    clientToken: 'pubd982a5aa7d97884078cc0e3417917ae7',
    service: 'mobile-app'
  },
  web: {
    applicationId: '74fc4786-c9e6-406d-a18f-8316dbfc258e',
    clientToken: 'pub30a98fa3adf475994f6361d25627a238',
    service: 'web-app'
  }
};

const LOGS_CONFIG = {
  forwardErrorsToLogs: true,
  // Deliberate breadcrumbs go through logStep, not console — see @nbx/frontend-helpers/logger.
  forwardConsoleLogs: ['error', 'warn'],
  sessionSampleRate: 100,
  sessionReplaySampleRate: 0,
  beforeSend: redactSensitiveUrls
};

const setup = async config => {
  if (process.env.NODE_ENV === 'development') {
    return;
  }

  datadogRum.init(config);
  datadogLogs.init({ ...config, ...LOGS_CONFIG });

  // Only the opaque internal id — never email, name or national id.
  const setUser = user => {
    if (user?.id) {
      datadogRum.setUser({ id: user.id });
      datadogLogs.setUser({ id: user.id });
    } else {
      datadogRum.clearUser();
      datadogLogs.clearUser();
    }
  };

  // Login happens long after init, so the stored user only covers a returning session.
  window.addEventListener(authTypes.AUTH_USER_CHANGED, event => setUser(event.detail));

  try {
    const userJSON = await NBXStorage.getItem('user');
    setUser(userJSON ? JSON.parse(userJSON) : null);
  } catch (e) {
    datadogLogs.logger.warn('Cannot get nbx user to attribute telemetry', e);
  }
};

export const setupDatadogWebApp = () =>
  setup({
    ...RUM_CONFIG.base,
    ...RUM_CONFIG.web,
    // eslint-disable-next-line no-undef
    version: __SHORT_COMMIT_HASH__
  });

export const setupDatadogMobileApp = () => {
  const platform = Capacitor.getPlatform();
  setup({
    ...RUM_CONFIG.base,
    ...RUM_CONFIG.mobile,
    // eslint-disable-next-line no-undef
    version: `${platform}.${__SHORT_COMMIT_HASH__}`
  });
};
